import { Injectable, inject } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { IEnv } from '@tnq/shared/types';

const NO_INDEX = 'noindex, nofollow';

@Injectable({
  providedIn: 'root',
})
export class RobotsMetaService {
  private readonly meta = inject(Meta);

  initialize(environment: IEnv): void {
    if (!environment.robots) {
      return;
    }

    const { robots } = environment;

    const robotsContent = this.buildRobotsContent(robots);

    this.meta.updateTag({ name: 'robots', content: robotsContent });

    if (robots.googleBot) {
      const googleBotContent = this.buildGoogleBotContent(robots.googleBot);
      this.meta.updateTag({ name: 'googlebot', content: googleBotContent });
    }
  }

  /**
   * Keeps a page out of the index whatever the environment says. For pages that
   * only exist behind a session and have nothing to offer a crawler — the upsell
   * flow, which is opened from the app with a token.
   */
  applyNoIndex(): void {
    this.meta.updateTag({ name: 'robots', content: NO_INDEX });
    this.meta.updateTag({ name: 'googlebot', content: NO_INDEX });
  }

  private buildRobotsContent(robots: IEnv['robots']): string {
    if (!robots) return '';

    const parts: string[] = [];

    if (robots.index === false) parts.push('noindex');
    if (robots.follow === false) parts.push('nofollow');
    if (robots.nosnippet) parts.push('nosnippet');
    if (robots.noarchive) parts.push('noarchive');
    if (robots.nocache) parts.push('nocache');
    if (robots.noimageindex) parts.push('noimageindex');
    if (robots.nositelinkssearchbox) parts.push('nositelinkssearchbox');

    return parts.join(', ');
  }

  private buildGoogleBotContent(
    googleBot: NonNullable<IEnv['robots']>['googleBot'],
  ): string {
    if (!googleBot) return '';

    const parts: string[] = [];

    if (googleBot.index === false) parts.push('noindex');
    if (googleBot.follow === false) parts.push('nofollow');
    if (googleBot.nosnippet) parts.push('nosnippet');
    if (googleBot.noarchive) parts.push('noarchive');
    if (googleBot.nocache) parts.push('nocache');
    if (googleBot.noimageindex) parts.push('noimageindex');
    if (googleBot.nositelinkssearchbox) parts.push('nositelinkssearchbox');

    return parts.join(', ');
  }
}
